// path: src/context/Context.js
'use client'
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react'
import { useSession } from 'next-auth/react'
import { toast } from 'react-toastify'
import styles from './Context.module.css'
import { getMemberCard } from '@/lib/getMemberCard'

const dataContext = React.createContext()
export const useContextElement = () => useContext(dataContext)

// GTM
const pushDataLayerEvent = (eventName, ecommerce = null, extra = {}) => {
  if (typeof window === 'undefined') return

  window.dataLayer = window.dataLayer || []

  // ecommerce 事件建議先清空，避免上一個 ecommerce 資料殘留
  if (ecommerce) {
    window.dataLayer.push({ ecommerce: null })
  }

  window.dataLayer.push({
    event: eventName,
    ...(ecommerce ? { ecommerce } : {}),
    ...extra,
  })
}

const toGA4Items = (items = []) =>
  items.map((x) => ({
    item_id: String(x.ProductID ?? ''),
    item_name: x.ProductName || '',
    item_variant: x.SkuID || '',
    price: Number(x.Price || 0),
    quantity: Number(x.Qty || 1),
    group_buying_token: x.GroupBuyingToken || '',
    is_preorder: Boolean(x.IsPreorder),
  }))

const pushAddToCartTracking = async (items = []) => {
  const responseStoreInfo = await fetch(`/api/online/get_StoreInfo`)
  const list = Array.isArray(items) ? items : [items]
  const validItems = list.filter((x) => x && Number(x.Qty || 0) > 0)

  if (!validItems.length) return

  const cartValue = validItems.reduce(
    (sum, x) => sum + Number(x.Price || 0) * Number(x.Qty || 1),
    0
  )

  // =========================
  // GTM / GA4
  // =========================
  const ga4Data = {
    currency: 'TWD',
    value: cartValue,
    items: toGA4Items(validItems),
  }

  pushDataLayerEvent('add_to_cart', ga4Data)

  writeMarketingEventLog('GA4', 'add_to_cart', ga4Data)

  try {
    const storeInfo = await responseStoreInfo.json()

    const adsId = storeInfo?.Data?.GoogleAdsID
    const labelAddToCart = storeInfo?.Data?.LabelAddToCart
    const facebookPixelId = storeInfo?.Data?.FacebookPixelID

    if (typeof window === 'undefined') return

    // =========================
    // Google Ads
    // =========================
    if (adsId && labelAddToCart) {
      const googleAdsData = {
        send_to: `AW-${adsId}/${labelAddToCart}`,
        value: cartValue,
        currency: 'TWD',
      }

      window.gtag?.('event', 'conversion', googleAdsData)

      writeMarketingEventLog('GoogleAds', 'conversion', googleAdsData)
    }

    // =========================
    // Facebook Pixel
    // =========================
    if (facebookPixelId && typeof window.fbq === 'function') {
      const facebookData = {
        content_ids: validItems.map((x) =>
          String(x.GoodListID || x.SkuID || x.GoodID || x.ProductID)
        ),

        content_type: 'product',

        contents: validItems.map((x) => ({
          id: String(x.GoodListID || x.SkuID || x.GoodID || x.ProductID),
          quantity: Number(x.Qty || 1),
          item_price: Number(x.Price || 0),
        })),

        value: cartValue,
        currency: 'TWD',
      }

      window.fbq('track', 'AddToCart', facebookData)

      writeMarketingEventLog('FacebookPixel', 'AddToCart', facebookData)
    }
  } catch (error) {
    console.warn('Add to cart marketing event failed:', error)
  }
}

// 廣告 Log
const writeMarketingEventLog = async (type, eventName, data) => {
  try {
    await fetch('/api/marketingEvent/log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        Type: type,
        EventName: eventName,
        Data: data,
      }),
    })
  } catch (error) {
    console.warn('Marketing Event log failed:', error)
  }
}

/** 將各種來源的欄位，統一成前端購物車使用的 shape */
function normalizeCartItem(x) {
  if (!x) return null
  const qty = Number(x.Qty)
  const price = Number(
    x.Price ?? x.PriceFromProduct ?? x.newPrice ?? x.currentPrice ?? 0
  )

  return {
    ProductID: x.ProductID,
    ProductName: x.ProductName,
    Qty: qty,
    SuggestPrice: x.SuggestPrice ?? 0,
    Price: Number.isFinite(price) ? price : 0,
    Cost: x.Cost ?? 0,
    PriceFromProduct: x.PriceFromProduct ?? price,
    PictureData: Array.isArray(x.PictureData)
      ? x.PictureData
      : x.images
        ? x.images.map((src) => ({ Name: src }))
        : [],
    SkuID: x.SkuID,
    SkuName: x.SkuName,
    Status: x.Status ?? 1,
    InventoryQty: x.InventoryQty ?? 0,
    LimitedQty: x.LimitedQty ?? 0,
    ProductOption: x.ProductOption ?? [],
    IsGroupBuying: x.IsGroupBuying ?? false,
    GroupBuyingToken: x.GroupBuyingToken ?? '',
    IsPreorder: Boolean(x.IsPreorder),
    ParentID: x.ParentID ?? '',
  }
}

export default function Context({ children }) {
  const { data: session, status } = useSession()

  const [cartProducts, setCartProducts] = useState([])
  const [wishList, setWishList] = useState([])
  const [orderMessage, setOrderMessage] = useState(0)
  const [coupon, setCoupon] = useState([])
  const [points, setPoints] = useState([])
  const [quickViewItem, setQuickViewItem] = useState({})

  // 控制彈出框
  const [dialog, setDialog] = useState({
    show: false,
    title: '',
    message: '',
    type: 'success', // success | error | info
  })

  const openDialog = ({ title, message, type = 'success' }) => {
    setDialog({
      show: true,
      title,
      message,
      type,
    })

    setTimeout(() => {
      setDialog((prev) => ({
        ...prev,
        show: false,
      }))
    }, 1500)
  }

  const closeDialog = () => {
    setDialog((prev) => ({
      ...prev,
      show: false,
    }))
  }

  const isLogin =
    status === 'authenticated' &&
    !!(session?.MemberID || session?.user?.MemberID)

  const getMemberId = () =>
    Number(session?.MemberID ?? session?.user?.MemberID ?? 1)

  // ✅ 計算總金額
  const totalPrice = useMemo(
    () =>
      cartProducts.reduce(
        (sum, p) => sum + (Number(p.Price) || 0) * (Number(p.Qty) || 1),
        0
      ),
    [cartProducts]
  )

  // ✅ 獲取願望清單
  const fetchWishlist = useCallback(async () => {
    try {
      const res = await fetch(`/api/product/get_WishList`)
      const data = await res.json()
      if (data.Data) {
        setWishList(data.Data)
      }
    } catch (error) {
      console.error('載入願望清單失敗:', error)
    }
  }, [])

  const fetchOrderMessageCount = useCallback(async () => {
    try {
      const res = await fetch(`/api/order/get_OrderMassageCount`)
      const data = await res.json()
      if (data.Success) {
        setOrderMessage(data.Count)
      }
    } catch (error) {
      console.error('載入訂單訊息數量失敗:', error)
    }
  }, [])

  const toBool = (value) => {
    if (value === true) return true
    if (value === false) return false
    if (value === 1) return true
    if (value === 0) return false

    const text = String(value ?? '')
      .trim()
      .toLowerCase()
    return text === 'true' || text === '1'
  }

  const fetchCoupon = useCallback(async () => {
    try {
      const res = await fetch(`/api/member/get_MemberCoupon`)
      const data = await res.json()
      if (data) {
        setCoupon(data)
      }
    } catch (error) {
      console.error('載入優惠券失敗:', error)
    }
  }, [])

  const fetchPoints = useCallback(async () => {
    try {
      const res = await fetch(`/api/member/get_BonusPoints`)
      const data = await res.json()
      if (data.points) {
        setPoints(data.points)
      }
    } catch (error) {
      console.error('載入點數失敗:', error)
    }
  }, [])

  // ✅ 覆蓋整份購物車（Drawer 開啟時用）
  const replaceCart = useCallback((serverList = []) => {
    const list = Array.isArray(serverList)
      ? serverList.map(normalizeCartItem).filter(Boolean)
      : []
    setCartProducts(list)
  }, [])

  // 失敗時回補：重新拉購物車
  const refetchCart = useCallback(async () => {
    try {
      const items = await fetchDataCartProduct()
      if (Array.isArray(items)) {
        replaceCart(items)
      }
    } catch (e) {
      console.error('refetch cart failed:', e)
    }
  }, [replaceCart])

  // ✅ 載入購物車與 wishlist（登入狀態）
  useEffect(() => {
    if (status === 'loading') return

    const loadCart = async () => {
      try {
        let items = []

        if (isLogin) {
          items = await fetchDataCartProduct()

          const localItems = JSON.parse(
            localStorage.getItem('cartList') || '[]'
          )
          const priceChanged = items.some((newItem) => {
            const oldItem = localItems.find(
              (old) =>
                old.ProductID === newItem.ProductID &&
                (old.SkuID || '') === (newItem.SkuID || '')
            )
            return oldItem && oldItem.Price !== newItem.Price
          })

          if (priceChanged) {
            toast.warning('有商品價格已更新，請確認購物車內容')
          }

          localStorage.removeItem('cartList')
        } else {
          items = JSON.parse(localStorage.getItem('cartList') || '[]')
        }

        if (Array.isArray(items)) {
          const normalizedItems = items.map(normalizeCartItem).filter(Boolean)
          setCartProducts(normalizedItems)
        }
      } catch (e) {
        console.error('購物車載入失敗:', e)
      }
    }

    if (isLogin) {
      fetchOrderMessageCount()
    }

    loadCart()
    fetchWishlist()
    fetchCoupon()
    fetchPoints()
  }, [status, isLogin])

  // ✅ 登出時清空購物車與 wishlist
  useEffect(() => {
    if (status === 'unauthenticated') {
      setCartProducts([])
      setWishList([])
      try {
        localStorage.removeItem('cartList')
      } catch {}
    }
  }, [status])

  // ✅ guest 願望清單儲存
  useEffect(() => {
    localStorage.setItem('wishlist', JSON.stringify(wishList))
  }, [wishList])

  // ✅ guest 購物車儲存
  useEffect(() => {
    if (!isLogin) {
      localStorage.setItem('cartList', JSON.stringify(cartProducts))
    }
  }, [cartProducts, isLogin])

  // ✅ 加入購物車（接收整個商品物件）
  const addProductToCart = async ({ ProductID, updated }) => {
    if (!updated?.ProductID) return

    const newItemIsPreorder = toBool(updated.IsPreorder)

    // ✅ 先取得目前後端購物車最新資料
    let currentCartData = []

    if (isLogin) {
      currentCartData = await fetchDataCartProduct()
    } else {
      currentCartData = cartProducts
    }

    const normalizedCartData = Array.isArray(currentCartData)
      ? currentCartData.map(normalizeCartItem).filter(Boolean)
      : []

    // ✅ 只拿 Qty > 0 的有效商品判斷
    const validCartItems = normalizedCartData.filter(
      (p) => Number(p.Qty || 0) > 0
    )

    // ✅ 檢查購物車是否已有不同 IsPreorder 的商品
    const hasDifferentPreorderItem = validCartItems.some(
      (p) => toBool(p.IsPreorder) !== newItemIsPreorder
    )

    if (hasDifferentPreorderItem) {
      openDialog({
        title: '',
        message: '預購商品與一般商品不可放在同一個購物車',
        type: 'error',
      })
      return
    }

    // ✅ 用最新購物車資料判斷是否已存在
    const exist = normalizedCartData.find(
      (p) =>
        String(p.ProductID) === String(ProductID) &&
        String(p.SkuID ?? '') === String(updated.SkuID ?? '')
    )

    const item = {
      ProductID: updated.ProductID,
      ProductName: updated.ProductName,
      Qty: exist ? updated.Qty : updated.Qty,
      SuggestPrice: updated.SuggestPrice,
      Price: updated.Price,
      Cost: updated.Cost,
      PriceFromProduct: updated.Price,
      PictureData: updated.PictureData,
      SkuID: updated.SkuID,
      SkuName: updated.SkuName,
      Status: updated.Status,
      InventoryQty: updated.InventoryQty,
      LimitedQty: updated.LimitedQty,
      ProductOption: updated.ProductOption ?? [],
      IsGroupBuying: updated.IsGroupBuying ?? false,
      GroupBuyingToken: updated.GroupBuyingToken ?? '',
      IsPreorder: newItemIsPreorder,
    }

    let result = {
      success: true,
      message: '',
    }

    if (isLogin) {
      result = exist
        ? await postUpdateCartProduct(
            item.ProductID,
            item.Qty,
            item.SkuID,
            item.Price,
            item.GroupBuyingToken,
            item.IsPreorder
          )
        : await postAddCartProductBatch([item])
    } else {
      const nextCartProducts = exist
        ? cartProducts.map((p) =>
            String(p.ProductID) === String(item.ProductID) &&
            String(p.SkuID ?? '') === String(item.SkuID ?? '')
              ? { ...p, Qty: item.Qty }
              : p
          )
        : [...cartProducts, item]

      setCartProducts(nextCartProducts)
    }

    if (result) {
      if (isLogin) {
        const latestCartData = await fetchDataCartProduct()
        setCartProducts(
          Array.isArray(latestCartData)
            ? latestCartData.map(normalizeCartItem).filter(Boolean)
            : []
        )
      }

      openDialog({
        title: '',
        message: exist ? '購物車數量已更新' : '加入購物車成功',
        type: 'success',
      })
    } else {
      openDialog({
        title: '',
        message:
          result.message || (exist ? '更新購物車失敗' : '加入購物車失敗'),
        type: 'error',
      })
    }

    await pushAddToCartTracking([item])
  }

  // ✅ 更新數量（串後端 + 樂觀更新 + 失敗回補）
  const [updatingSet, setUpdatingSet] = useState(() => new Set())
  const updateCartItemQty = useCallback(
    async (ProductID, SkuID = '', Qty = 1, GroupBuyingToken = '', Price) => {
      const pid = Number(ProductID)
      const sid = SkuID || ''
      const newQty = Math.max(1, Number(Qty) || 1)
      const key = `${pid}:${sid}`
      const price = Price

      if (updatingSet.has(key)) return

      setCartProducts((prev) =>
        prev.map((p) =>
          p.ProductID === pid && (p.SkuID ?? '') === sid
            ? { ...p, Qty: newQty }
            : p
        )
      )
      setUpdatingSet((s) => new Set(s).add(key))

      try {
        let success = false
        if (isLogin) {
          success = await postUpdateCartProduct(
            pid,
            newQty,
            sid,
            price,
            GroupBuyingToken
          )
        } else {
          success = true
        }

        if (!success) {
          throw new Error('更新失敗')
        }
      } catch (e) {
        console.error('update cart qty failed:', e)
        toast.error('更新數量失敗，已還原')
        await refetchCart()
      } finally {
        setUpdatingSet((s) => {
          const ns = new Set(s)
          ns.delete(key)
          return ns
        })
      }
    },
    [refetchCart, updatingSet, isLogin]
  )

  // ✅ 移除購物車商品（串後端 + 樂觀更新 + 禁重複）
  const [removingSet, setRemovingSet] = useState(() => new Set())
  const isRemoving = useCallback(
    (ProductID, SkuID = '') => removingSet.has(`${ProductID}:${SkuID ?? ''}`),
    [removingSet]
  )

  const removeProductFromCart = useCallback(
    async (ProductID, SkuID = '') => {
      const key = `${ProductID}:${SkuID ?? ''}`
      if (removingSet.has(key)) return

      const snapshot = cartProducts
      // 💡 樂觀更新：先讓前端畫面把該列主商品隱藏
      const next = snapshot.filter(
        (p) => !(p.ProductID === ProductID && (p.SkuID ?? '') === (SkuID ?? ''))
      )
      setCartProducts(next)
      setRemovingSet((s) => new Set(s).add(key))

      try {
        let success = true
        if (isLogin) {
          // 呼叫後端刪除 API（後端會把主商品與配件一起從資料庫砍掉）
          success = await postRemoveCartProduct(ProductID, SkuID)
        } else {
          localStorage.setItem('cartList', JSON.stringify(next))
        }

        if (!success) {
          throw new Error('移除失敗')
        }

        if (isLogin) {
          // 🔄 確定刪除成功後，重新向後端拉取最新的購物車資料
          const latestCartData = await fetchDataCartProduct()

          // 🔄 將包含配件一併被刪除後的正確最新資料，塞入狀態中
          setCartProducts(latestCartData)
        } else {
          // 未登入狀態（LocalStorage），則直接使用過濾後的 next
          setCartProducts(next)
        }
      } catch (e) {
        console.error('remove_product_from_cart failed:', e)
        // ❌ 發生錯誤時，回滾成原本的購物車資料快照
        setCartProducts(snapshot)
        toast.error('移除失敗，請稍後再試')
      } finally {
        setRemovingSet((s) => {
          const ns = new Set(s)
          ns.delete(key)
          return ns
        })
      }
    },
    [cartProducts, isLogin, removingSet] // 💡 記得在依賴陣列中移除舊的 refetchCart 確保效能
  )

  const isAddedToCartProducts = (ProductID) =>
    cartProducts.some((item) => item.ProductID === ProductID)

  const toggleWishlist = async (product) => {
    if (!isLogin) {
      openDialog({
        title: '',
        message: '請先登入',
        type: 'info',
      })
      return
    }

    const isAdded = wishList.some(
      (item) => item.ProductID === product.ProductID
    )

    const apiUrl = isAdded
      ? '/api/product/del_ProductFromWishlist'
      : '/api/product/add_ProductToWishlist'

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ProductID: product.ProductID }),
      })

      const json = await response.json()

      if (json?.Error) {
        openDialog({
          title: '',
          message: '願望清單操作失敗',
          type: 'error',
        })
        return
      }

      if (!isAdded) {
        pushDataLayerEvent('add_to_wishlist', {
          currency: 'TWD',
          value: Number(product.Price || product.SuggestPrice || 0),
          items: [
            {
              item_id: String(product.ProductID),
              item_name: product.ProductName || '',
              price: Number(product.Price || product.SuggestPrice || 0),
              quantity: 1,
            },
          ],
        })
      }

      setTimeout(() => {
        fetchWishlist()
      }, 500)

      openDialog({
        title: '',
        message: isAdded ? '移除收藏成功' : '加入收藏成功',
        type: 'success',
      })
    } catch (err) {
      console.error('toggleWishlist error:', err)

      openDialog({
        title: '',
        message: '願望清單操作失敗',
        type: 'error',
      })
    }
  }

  // ✅ 檢查購物車庫存
  const checkCartInventory = useCallback(async () => {
    // 未登入不用打後端購物車驗證
    if (!isLogin) {
      return {
        success: true,
        hasRemovedProduct: false,
        removedCount: 0,
        removedProducts: [],
      }
    }

    try {
      const response = await fetch('/api/product/check_Cart_Inventory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          StoreNumber: Number(process.env.NEXT_PUBLIC_STORE_NUMBER),
        }),
      })

      const json = await response.json()

      if (!response.ok || json?.Error || json?.error) {
        console.error(
          '檢查購物車庫存失敗:',
          json?.Error || json?.error || json?.Message
        )

        return {
          success: false,
          hasRemovedProduct: false,
          removedCount: 0,
          removedProducts: [],
        }
      }

      const data = json?.Data || {}

      const removedProducts = Array.isArray(data.RemovedProducts)
        ? data.RemovedProducts
        : []

      // 有商品被後端清掉時，重新抓最新購物車
      if (removedProducts.length > 0) {
        const latestCartData = await fetchDataCartProduct()

        setCartProducts(
          Array.isArray(latestCartData)
            ? latestCartData.map(normalizeCartItem).filter(Boolean)
            : []
        )
      }

      return {
        success: true,
        hasRemovedProduct:
          data.HasRemovedProduct === true || removedProducts.length > 0,
        removedCount: Number(data.RemovedCount || removedProducts.length),
        removedProducts,
      }
    } catch (error) {
      console.error('檢查購物車庫存失敗:', error)

      return {
        success: false,
        hasRemovedProduct: false,
        removedCount: 0,
        removedProducts: [],
      }
    }
  }, [isLogin])

  // ✅ 檢查購物車價格
  const checkCartPrice = useCallback(async () => {
    if (!isLogin) {
      return {
        success: true,
        hasPriceChanged: false,
        changedCount: 0,
        changedProducts: [],
      }
    }

    try {
      const response = await fetch('/api/product/check_Cart_Price', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const json = await response.json().catch(() => ({}))

      if (!response.ok || json?.Error || json?.error) {
        console.error(
          '檢查購物車價格失敗:',
          json?.Error || json?.error || json?.Message
        )

        return {
          success: false,
          hasPriceChanged: false,
          changedCount: 0,
          changedProducts: [],
        }
      }

      const data = json || {}

      const changedProducts = Array.isArray(data.ChangedProducts)
        ? data.ChangedProducts
        : []

      const changedCount = Number(
        data.ChangedCount ?? data.UpdateCount ?? changedProducts.length ?? 0
      )

      const hasPriceChanged =
        data.HasPriceChanged === true ||
        changedCount > 0 ||
        changedProducts.length > 0

      // 後端已經更新購物車價格
      // 所以前端重新抓一次最新購物車
      if (hasPriceChanged) {
        const latestCartData = await fetchDataCartProduct()

        setCartProducts(
          Array.isArray(latestCartData)
            ? latestCartData.map(normalizeCartItem).filter(Boolean)
            : []
        )
      }

      return {
        success: true,
        hasPriceChanged,
        changedCount,
        changedProducts,
      }
    } catch (error) {
      console.error('檢查購物車價格失敗:', error)

      return {
        success: false,
        hasPriceChanged: false,
        changedCount: 0,
        changedProducts: [],
      }
    }
  }, [isLogin])

  const isAddedtoWishlist = (ProductID) =>
    wishList.some((w) => w.ProductID === ProductID)

  const contextElement = {
    cartProducts,
    setCartProducts,
    totalPrice,
    addProductToCart,
    updateCartItemQty,
    removeProductFromCart,
    replaceCart,
    isAddedToCartProducts,
    isRemoving,

    wishList,
    toggleWishlist,
    isAddedtoWishlist,

    // 訂單詢問訊息量
    orderMessage,

    coupon,
    points,

    quickViewItem,
    setQuickViewItem,
    isLogin,
    session,
    // 寫廣告的 Log
    writeMarketingEventLog,
    checkCartInventory,
    checkCartPrice,
  }

  return (
    <dataContext.Provider value={contextElement}>
      {children}

      {/* 彈出框 */}
      {dialog.show && (
        <div className={styles.dialogBackdrop} onClick={closeDialog}>
          <div
            className={styles.dialogBox}
            onClick={(e) => e.stopPropagation()}
          >
            <span
              className={`${styles.dialogIcon} ${
                dialog.type === 'error'
                  ? styles.error
                  : dialog.type === 'info'
                    ? styles.info
                    : styles.success
              }`}
            >
              {dialog.type === 'error' ? '!' : '✓'}
            </span>

            <span className={styles.dialogMessage}>{dialog.message}</span>
          </div>
        </div>
      )}
    </dataContext.Provider>
  )
}

// 🔽 外部工具函數區域

const fetchDataCartProduct = async () => {
  try {
    const response = await fetch(`/api/product/get_CartProduct`)
    const json = await response.json()
    if (json.Error) {
      alert('無法載入購物車, 請稍後再試')
      return []
    }
    return json.Data || []
  } catch (err) {
    console.error('Fetch error:', err)
    return []
  }
}

// ✅ 加入購物車時帶上 GroupBuyingToken
const postAddCartProductBatch = async (ProductData) => {
  try {
    const response = await fetch('/api/product/add_ProductToCart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ProductData }),
    })

    const json = await response.json()

    if (json.Error) {
      console.error('加入購物車失敗:', json.Error)
      return {
        success: false,
        message: json.Error,
      }
    }

    return {
      success: true,
      message: '',
    }
  } catch (err) {
    console.error('加入購物車失敗:', err)

    return {
      success: false,
      message: err?.message || '加入購物車失敗',
    }
  }
}

// ✅ 更新購物車時帶上 GroupBuyingToken
const postUpdateCartProduct = async (
  ProductID,
  Qty,
  SkuID,
  Price,
  GroupBuyingToken = '',
  IsPreorder
) => {
  try {
    const response = await fetch('/api/product/update_ProductToCart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ProductID,
        Qty,
        ...(SkuID ? { SkuID } : {}),
        Price,
        ...(GroupBuyingToken ? { GroupBuyingToken } : {}),
        IsPreorder,
      }),
    })
    const json = await response.json()

    if (json.Error) {
      console.error('更新購物車失敗:', err)
      return false
    }

    return true
  } catch (err) {
    console.error('更新購物車失敗:', err)
    return false
  }
}

const postRemoveCartProduct = async (ProductID, SkuID) => {
  try {
    const response = await fetch('/api/product/del_ProductFromCart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ProductID,
        SkuID,
      }),
    })
    const json = await response.json()
    if (json.Error) {
      alert(`移除購物車失敗：${json.Error}`)
      return false
    }
    return true
  } catch (err) {
    console.error('移除購物車失敗:', err)
    alert('移除購物車失敗')
    return false
  }
}
